import { useEffect, useMemo, useState } from "react";
import { X, Check } from "lucide-react";
import { toast } from "sonner";
import { api, inr, formatApiError } from "../lib/api";

const inputCls = "bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37]";
const yob = (p) => (p.dob || "").slice(0, 4);
const inBatch = (p, b) => p.batch === b || (p.batches || []).includes(b);

const PlayerList = ({ list, picked, toggle }) => (
  <div className="max-h-64 overflow-y-auto border border-white/10 divide-y divide-white/5" data-testid="add-dues-player-list">
    {list.map((p) => (
      <button type="button" key={p.player_id} onClick={() => toggle(p.player_id)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-white/5 transition-colors duration-150"
        data-testid={`add-dues-player-${p.player_id}`}>
        <span className="text-sm">{p.name} <span className="text-white/40 text-xs ml-1">{p.batch || "No batch"}{yob(p) ? ` · ${yob(p)}` : ""}</span></span>
        <span className={`w-5 h-5 border flex items-center justify-center ${picked.has(p.player_id) ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-white/30"}`}>
          {picked.has(p.player_id) && <Check size={13} />}
        </span>
      </button>
    ))}
    {list.length === 0 && <p className="text-white/40 text-sm p-4 text-center">No players match.</p>}
  </div>
);

export const AddDuesModal = ({ preselect = [], onClose, onSaved }) => {
  const [players, setPlayers] = useState([]);
  const [picked, setPicked] = useState(new Set(preselect));
  const [q, setQ] = useState("");
  const [batch, setBatch] = useState("");
  const [year, setYear] = useState("");
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/players").then((r) => setPlayers(r.data.filter((p) => p.status === "Active" || preselect.includes(p.player_id)))).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const batches = useMemo(() => [...new Set(players.flatMap((p) => [p.batch, ...(p.batches || [])]).filter(Boolean))].sort(), [players]);
  const years = useMemo(() => [...new Set(players.map(yob).filter(Boolean))].sort(), [players]);
  const list = players.filter((p) => (!q || p.name.toLowerCase().includes(q.toLowerCase())) && (!batch || inBatch(p, batch)) && (!year || yob(p) === year));

  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = list.length > 0 && list.every((p) => picked.has(p.player_id));
  const toggleAll = () => setPicked((s) => { const n = new Set(s); list.forEach((p) => (allOn ? n.delete(p.player_id) : n.add(p.player_id))); return n; });

  const submit = async (e) => {
    e.preventDefault();
    if (!picked.size) { toast.error("Select at least one player"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/extra-charges", { player_ids: [...picked], amount: Number(amount), label });
      toast.success(`Added ${inr(Number(amount))} "${label}" for ${data.added} player${data.added === 1 ? "" : "s"}`);
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto py-10 px-4" onClick={onClose} data-testid="add-dues-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="max-w-lg mx-auto bg-[#0A0A0A] border border-white/10 p-6 sm:p-8">
        <div className="flex justify-between items-start mb-2">
          <h2 className="font-heading text-xl font-black">Add Dues</h2>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white" data-testid="add-dues-close"><X size={20} /></button>
        </div>
        <p className="text-white/50 text-sm mb-5">Due immediately. Shows in Dues, Reminders, the portal and receipts.</p>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <input required placeholder="What is it for? (e.g. Goa Cup tournament)" value={label} onChange={(e) => setLabel(e.target.value)} className={inputCls + " col-span-2"} data-testid="add-dues-label" />
          <input required type="number" min="1" placeholder="Amount ₹" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} data-testid="add-dues-amount" />
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <input placeholder="Search name" value={q} onChange={(e) => setQ(e.target.value)} className={inputCls + " flex-1 min-w-[140px]"} data-testid="add-dues-search" />
          <select value={batch} onChange={(e) => setBatch(e.target.value)} className={inputCls} data-testid="add-dues-batch">
            <option value="">All batches</option>
            {batches.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(e.target.value)} className={inputCls} data-testid="add-dues-yob">
            <option value="">Any birth year</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex justify-between items-center mb-2 text-xs">
          <button type="button" onClick={toggleAll} className="uppercase tracking-widest text-[#D4AF37] hover:text-[#F3D059]" data-testid="add-dues-toggle-all">{allOn ? "Unselect shown" : `Select all shown (${list.length})`}</button>
          <span className="text-white/50" data-testid="add-dues-selected-count">{picked.size} selected</span>
        </div>
        <PlayerList list={list} picked={picked} toggle={toggle} />
        <button type="submit" disabled={busy} className="mt-5 w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200 disabled:opacity-50" data-testid="add-dues-save">
          {busy ? "Adding..." : `Add to ${picked.size} player${picked.size === 1 ? "" : "s"}`}
        </button>
      </form>
    </div>
  );
};
